/**
 * External Dependencies
 */
import assign from 'lodash.assign';

/**
 * WordPress Dependencies
 */
const {createHigherOrderComponent} = wp.compose;
const {Fragment} = wp.element;
const {InspectorControls} = wp.blockEditor;
const {PanelBody, PanelRow, ToggleControl} = wp.components;
const {addFilter} = wp.hooks;
const {__} = wp.i18n;
const {dispatch} = wp.data;

/**
 * Add layout control attribute to block.
 *
 * @param {object} settings Current block settings.
 * @param {string} name Name of block.
 *
 * @returns {object} Modified block settings.
 */
const addAttributes = (settings, name) => {
	// The freeform (Classic Editor) block is incompatible because it does not
	// support custom attributes.
	if ( settings.name === 'core/freeform' ) {
		return settings;
	}

	const attributes = {
		responsiveBlockControl: {
			type: 'object',
			default: {
				mobile: false,
				tablet: false,
				desktop: false,
				wide: false,
			},
		},
	};

	// Use Lodash's assign to gracefully handle if attributes are undefined
	settings.attributes = assign(settings.attributes, attributes);

	return settings;
};

addFilter('blocks.registerBlockType', 'responsiveBlockControl/attribute', addAttributes);

/**
 * Create HOC to add layout control to inspector controls of block.
 */
const withVisibilityControl = createHigherOrderComponent((BlockEdit) => {
	return (props) => {
		const {
			clientId,
			attributes,
			isSelected,
		} = props;

		let {
			responsiveBlockControl,
		} = attributes;

		const handleChange = (device) => {
			const newValue = !responsiveBlockControl[device];

			delete responsiveBlockControl[device];

			const blockOptions = Object.assign({[device]: newValue}, responsiveBlockControl);

			dispatch('core/block-editor').updateBlockAttributes(clientId, {responsiveBlockControl: blockOptions});
		};

		if (typeof responsiveBlockControl === 'undefined') {
			responsiveBlockControl = [];
		}

		return (
			<Fragment>
				<BlockEdit {...props} />
				{isSelected &&
				<InspectorControls>
					<PanelBody
						title={__('Visibility')}
						initialOpen={false}
					>
						<PanelRow>
							<ToggleControl
								label={__('Hide on Mobile', 'responsive-block-control')}
								checked={!!responsiveBlockControl.mobile}
								onChange={() => handleChange('mobile')}
							/>
						</PanelRow>
						<PanelRow>
							<ToggleControl
								label={__('Hide on Tablet', 'responsive-block-control')}
								checked={!!responsiveBlockControl.tablet}
								onChange={() => handleChange('tablet')}
							/>
						</PanelRow>
						<PanelRow>
							<ToggleControl
								label={__('Hide on Desktop', 'responsive-block-control')}
								checked={!!responsiveBlockControl.desktop}
								onChange={() => handleChange('desktop')}
							/>
						</PanelRow>
						<PanelRow>
							<ToggleControl
								label={__('Hide on Wide', 'responsive-block-control')}
								checked={!!responsiveBlockControl.wide}
								onChange={() => handleChange('wide')}
							/>
						</PanelRow>
					</PanelBody>
				</InspectorControls>
				}
			</Fragment>
		);
	};
}, 'withVisibilityControl');

addFilter('editor.BlockEdit', 'responsiveBlockControl/control', withVisibilityControl);

// add a data attribute to the block wrapper "editor-block-list__block"
// this hook only fires for the BE/Admin View
const addWrapperDataAttribute = createHigherOrderComponent((BlockListBlock) => {
	return props => {
		const {
			responsiveBlockControl,
		} = props.attributes;

		let wrapperProps = props.wrapperProps;
		let customData = {};

		if (typeof responsiveBlockControl !== 'undefined') {
			let hiddenOn = [];
			for (const [breakpoint, value] of Object.entries(responsiveBlockControl)) {
				if (value === true) {
					hiddenOn.push(breakpoint);
				}
			}

			if (hiddenOn.length > 0) {
				customData = assign(customData, {'data-is-hidden-on': 'Hidden on ' + hiddenOn.join(' ')});
			}
		}

		if (Object.keys(customData).length !== 0) {
			wrapperProps = assign(wrapperProps, customData);
		}

		return <BlockListBlock {...props} wrapperProps={wrapperProps}/>;
	};
}, 'addWrapperDataAttribute');

addFilter(
	'editor.BlockListBlock',
	'responsiveBlockControl/addWrapperDataAttribute',
	addWrapperDataAttribute,
);
